// ─── Hero KPI ──────────────────────────────────────────────────────────
// Big closure-rate number in Raven h2 (60/700/1px LS) with a tight meta
// column to its right. Whole block is a clickable affordance — opens the
// closed-status drill-down.

import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ArrowUp, ArrowDown } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import CountChip from "../../components/CountChip";

interface HeroKPIProps {
  closureRate: number;
  priorRate: number | null;
  total: number;
  closed: number;
  to?: string;
  // Show prior-period delta only when both windows have meaningful sample
  // size. Below this threshold the +/- swings are noise.
  minSampleForDelta: number;
}

export default function HeroKPI({
  closureRate,
  priorRate,
  total,
  closed,
  to,
  minSampleForDelta,
}: HeroKPIProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const showDelta =
    priorRate !== null && total >= minSampleForDelta && closed >= 0;
  const delta = showDelta ? closureRate - priorRate! : 0;
  const deltaColor =
    !showDelta || delta === 0
      ? theme.palette.text.secondary
      : delta > 0
        ? theme.palette.success.main
        : theme.palette.error.main;
  const interactive = !!to;
  return (
    <Box
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? () => navigate(to!) : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(to!);
              }
            }
          : undefined
      }
      sx={{
        display: "flex",
        alignItems: "baseline",
        flexWrap: "wrap",
        gap: 2,
        cursor: interactive ? "pointer" : "default",
        borderRadius: 1,
        outline: "none",
        "&:focus-visible": interactive
          ? { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.3)}` }
          : undefined,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Typography
          variant="h2"
          sx={{ color: "primary.dark", lineHeight: 1 }}
        >
          {closureRate}
        </Typography>
        <Typography variant="h5" sx={{ color: "primary.dark" }}>
          %
        </Typography>
      </Box>
      <Stack spacing={0.75} sx={{ minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flexWrap: "wrap", rowGap: 0.5 }}
        >
          <Typography variant="overline" color="text.secondary">
            Closure Rate
          </Typography>
          <CountChip>{`${closed} / ${total} closed`}</CountChip>
        </Stack>
        {showDelta && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.25}
            sx={{ color: deltaColor }}
          >
            {delta > 0 ? (
              <RavenIcon icon={ArrowUp} sx={{ fontSize: 14 }} />
            ) : delta < 0 ? (
              <RavenIcon icon={ArrowDown} sx={{ fontSize: 14 }} />
            ) : null}
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {delta === 0 ? "0" : `${delta > 0 ? "+" : ""}${delta}`} pts
            </Typography>
            <Typography variant="caption" color="text.secondary">
              vs prior period
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
